import type Parse from 'parse';

import { Workspace, Member, type WorkspaceRepository } from '@telespot/domain';

import { ParseBaseRepository } from '../../parse-base.repository';
import { SoftDeletableTopology } from '../../parse.topology';
import { UserTopology } from '../accounts';

import { MemberTopology, ParseMemberMapper } from './parse-member.mapper';
import { ParseWorkspaceMapper, WorkspaceTopology } from './parse-workspace.mapper';
import { OrganizationTopology } from '../organization';
import { CaseTypeTopology } from '../protocols';
import { ObjectTopology } from '../../parse.topology';

const QUERY_PAGE_SIZE = 20;

export class ParseWorkspaceRepository extends ParseBaseRepository implements WorkspaceRepository {
  private readonly workspaceMapper: ParseWorkspaceMapper;
  private readonly memberMapper: ParseMemberMapper;

  constructor(parse: typeof Parse, options?: Parse.FullOptions) {
    super(parse, options);
    this.workspaceMapper = new ParseWorkspaceMapper(parse);
    this.memberMapper = new ParseMemberMapper(parse);
  }

  public async getById(id: string): Promise<Workspace> {
    const query = new this.parse.Query(WorkspaceTopology.TABLE);
    const parseWorkspace = await query.get(id, this.options);
    return this.workspaceMapper.toDomain(parseWorkspace);
  }

  public async findMemberInWorkspaceByName(workspaceId: string, username: string): Promise<Member> {
    const workspace = this.subclasses.getSubclass(WorkspaceTopology.TABLE).createWithoutData(workspaceId);

    const userQuery = new this.parse.Query(this.parse.User).equalTo(UserTopology.USERNAME, username);

    const parseMember = await new this.parse.Query(MemberTopology.TABLE)
      .matchesQuery(MemberTopology.USER, userQuery)
      .equalTo(MemberTopology.WORKSPACE, workspace)
      .include(MemberTopology.USER)
      .first(this.options);

    return parseMember ? this.memberMapper.toDomain(parseMember) : undefined;
  }

  public async list(
    organizationId: string,
    limit: number,
    caseTypeId?: string,
    searchTerm?: string,
    lastItemCreatedAt?: Date
  ): Promise<Workspace[]> {
    const query = this.buildWorkspaceQuery(organizationId, caseTypeId, searchTerm).descending(
      ObjectTopology.CREATED_AT
    );

    if (lastItemCreatedAt) query.lessThan(ObjectTopology.CREATED_AT, lastItemCreatedAt);
    query.limit(limit);

    const parseObjects = await query.find(this.options);

    return parseObjects.map((parseObject) => this.workspaceMapper.toDomain(parseObject));
  }

  public async count(organizationId: string, caseTypeId?: string, searchTerm?: string): Promise<number> {
    let total = 0;
    let lastObjectId: string | undefined;
    let hasMore = true;

    while (hasMore) {
      const query = this.buildWorkspaceQuery(organizationId, caseTypeId, searchTerm)
        .ascending(ObjectTopology.ID)
        .limit(QUERY_PAGE_SIZE);

      query.select(); // Only fetch objectIds to minimize data transfer

      if (lastObjectId) {
        query.greaterThan(ObjectTopology.ID, lastObjectId);
      }

      const results = await query.find(this.options);
      total += results.length;

      // Check if we got fewer results than the limit, indicating we've reached the end
      hasMore = results.length === QUERY_PAGE_SIZE;

      // Update lastObjectId for the next iteration
      if (hasMore && results.length > 0) {
        lastObjectId = results.at(-1).id;
      }
    }

    return total;
  }

  public async countByCaseType(caseTypeId: string): Promise<number> {
    const parseCaseType = this.subclasses.getSubclass(CaseTypeTopology.TABLE).createWithoutData(caseTypeId);

    let total = 0;
    let lastObjectId: string | undefined;
    let hasMore = true;

    while (hasMore) {
      const query = new this.parse.Query(WorkspaceTopology.TABLE)
        .equalTo(WorkspaceTopology.CASE_TYPE, parseCaseType)
        .ascending(ObjectTopology.ID)
        .limit(QUERY_PAGE_SIZE);

      query.select(); // Only fetch objectIds to minimize data transfer

      if (lastObjectId) {
        query.greaterThan(ObjectTopology.ID, lastObjectId);
      }

      const results = await query.find(this.options);
      total += results.length;

      // Check if we got fewer results than the limit, indicating we've reached the end
      hasMore = results.length === QUERY_PAGE_SIZE;

      // Update lastObjectId for the next iteration
      if (hasMore && results.length > 0) {
        lastObjectId = results.at(-1).id;
      }
    }

    return total;
  }

  public async findForCaseType(caseTypeId: string): Promise<Workspace[]> {
    const parseCaseType = this.subclasses.getSubclass(CaseTypeTopology.TABLE).createWithoutData(caseTypeId);

    const allWorkspaces: Workspace[] = [];
    let lastObjectId: string | undefined;
    let hasMore = true;

    while (hasMore) {
      const query = new this.parse.Query(WorkspaceTopology.TABLE)
        .equalTo(WorkspaceTopology.CASE_TYPE, parseCaseType)
        .ascending(ObjectTopology.ID)
        .limit(QUERY_PAGE_SIZE);

      if (lastObjectId) {
        query.greaterThan(ObjectTopology.ID, lastObjectId);
      }

      const results = await query.find(this.options);
      const workspaces = results.map((parseObject) => this.workspaceMapper.toDomain(parseObject));
      allWorkspaces.push(...workspaces);

      // Check if we got fewer results than the limit, indicating we've reached the end
      hasMore = results.length === QUERY_PAGE_SIZE;

      // Update lastObjectId for the next iteration
      if (hasMore && results.length > 0) {
        lastObjectId = results.at(-1).id;
      }
    }

    return allWorkspaces;
  }

  private buildWorkspaceQuery(organizationId: string, caseTypeId?: string, searchTerm?: string): Parse.Query {
    const organization = this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId);

    const query = new this.parse.Query(WorkspaceTopology.TABLE).equalTo(WorkspaceTopology.ORGANIZATION, organization);

    if (caseTypeId) {
      const parseCaseType = this.subclasses.getSubclass(CaseTypeTopology.TABLE).createWithoutData(caseTypeId);
      query.equalTo(WorkspaceTopology.CASE_TYPE, parseCaseType);
    }

    if (!!searchTerm && searchTerm !== '') {
      query.matches(WorkspaceTopology.NAME, new RegExp(this.escapeRegExp(searchTerm), 'i'));
    }

    return query;
  }

  public async listUserMemberships(
    userId: string,
    organizationId: string
  ): Promise<Array<{ workspace: Workspace; member: Member }>> {
    const parseOrganization = this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId);
    const parseUser = this.parse.User.createWithoutData(userId);

    const workspaceOrgQuery = new this.parse.Query(WorkspaceTopology.TABLE).equalTo(
      WorkspaceTopology.ORGANIZATION,
      parseOrganization
    );

    const allMemberships: Array<{ workspace: Workspace; member: Member }> = [];
    let lastObjectId: string | undefined;
    let hasMore = true;

    while (hasMore) {
      const memberQuery = this.buildMembershipQuery(parseUser, workspaceOrgQuery, lastObjectId);
      memberQuery.include(MemberTopology.WORKSPACE);

      const parseMemberships = await memberQuery.find(this.options);
      const mappedMemberships = parseMemberships
        .map((parseMembership) => {
          const parseWorkspace = parseMembership.get(MemberTopology.WORKSPACE);
          return {
            workspace: this.workspaceMapper.toDomain(parseWorkspace),
            member: this.memberMapper.toDomain(parseMembership),
          };
        })
        .filter((item): item is { workspace: Workspace; member: Member } => item !== null);

      allMemberships.push(...mappedMemberships);

      // Check if we got fewer results than the limit, indicating we've reached the end
      hasMore = parseMemberships.length === QUERY_PAGE_SIZE;

      // Update lastObjectId for the next iteration
      if (hasMore && parseMemberships.length > 0) {
        lastObjectId = parseMemberships.at(-1).id;
      }
    }

    return allMemberships;
  }

  public async countUserMemberships(userId: string, organizationId: string): Promise<number> {
    const parseOrganization = this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId);
    const parseUser = this.parse.User.createWithoutData(userId);

    const workspaceOrgQuery = new this.parse.Query(WorkspaceTopology.TABLE).equalTo(
      WorkspaceTopology.ORGANIZATION,
      parseOrganization
    );

    let total = 0;
    let lastObjectId: string | undefined;
    let hasMore = true;

    while (hasMore) {
      const memberQuery = this.buildMembershipQuery(parseUser, workspaceOrgQuery, lastObjectId);
      memberQuery.select(); // Only fetch objectIds to minimize data transfer

      const parseMemberships = await memberQuery.find(this.options);
      total += parseMemberships.length;

      // Check if we got fewer results than the limit, indicating we've reached the end
      hasMore = parseMemberships.length === QUERY_PAGE_SIZE;

      // Update lastObjectId for the next iteration
      if (hasMore && parseMemberships.length > 0) {
        lastObjectId = parseMemberships.at(-1).id;
      }
    }

    return total;
  }

  public async countMembers(workspaceId: string, searchTerm?: string): Promise<number> {
    const parseWorkspace = this.subclasses.getSubclass(WorkspaceTopology.TABLE).createWithoutData(workspaceId);

    let total = 0;
    let lastObjectId: string | undefined;
    let hasMore = true;

    while (hasMore) {
      const memberQuery = new this.parse.Query(MemberTopology.TABLE)
        .equalTo(MemberTopology.WORKSPACE, parseWorkspace)
        .ascending(ObjectTopology.ID)
        .limit(QUERY_PAGE_SIZE);

      if (searchTerm) {
        const userQuery = new this.parse.Query(this.parse.User).matches(
          UserTopology.USERNAME,
          new RegExp(this.escapeRegExp(searchTerm), 'i')
        );
        memberQuery.matchesQuery(MemberTopology.USER, userQuery);
      }

      memberQuery.select(); // Only fetch objectIds to minimize data transfer

      if (lastObjectId) {
        memberQuery.greaterThan(ObjectTopology.ID, lastObjectId);
      }

      const parseMemberships = await memberQuery.find(this.options);
      total += parseMemberships.length;

      hasMore = parseMemberships.length === QUERY_PAGE_SIZE;
      if (hasMore && parseMemberships.length > 0) {
        lastObjectId = parseMemberships.at(-1).id;
      }
    }

    return total;
  }

  private buildMembershipQuery(
    parseUser: Parse.User,
    workspaceOrgQuery: Parse.Query,
    lastObjectId?: string
  ): Parse.Query {
    const memberQuery = new this.parse.Query(MemberTopology.TABLE)
      .equalTo(MemberTopology.USER, parseUser)
      .exists(MemberTopology.WORKSPACE)
      .matchesQuery(MemberTopology.WORKSPACE, workspaceOrgQuery)
      .ascending(ObjectTopology.ID)
      .limit(QUERY_PAGE_SIZE);

    if (lastObjectId) {
      memberQuery.greaterThan(ObjectTopology.ID, lastObjectId);
    }

    return memberQuery;
  }

  public async searchUserMemberships(
    userId: string,
    workspaceIds: string[]
  ): Promise<Array<{ workspaceId: string; membershipId: string }>> {
    const parseUser = this.parse.User.createWithoutData(userId);
    const parseWorkspacesQuery = new this.parse.Query(WorkspaceTopology.TABLE).containedIn(
      ObjectTopology.ID,
      workspaceIds
    );
    const membershipsQuery = new this.parse.Query(MemberTopology.TABLE)
      .equalTo(MemberTopology.USER, parseUser)
      .exists(MemberTopology.WORKSPACE)
      .matchesQuery(MemberTopology.WORKSPACE, parseWorkspacesQuery)
      .select(MemberTopology.WORKSPACE);

    const parseMemberships = await membershipsQuery.find(this.options);

    return parseMemberships.map((parseMembership) => ({
      workspaceId: parseMembership.get(MemberTopology.WORKSPACE)?.id,
      membershipId: parseMembership.id,
    }));
  }

  public async createUserMemberships(
    userId: string,
    workspaceIds: string[],
    batchSize: number
  ): Promise<Array<{ workspaceId: string; membershipId: string }>> {
    const parseMemberships = [];
    for (const workspaceId of workspaceIds) {
      const parseWorkspace = this.parse.Object.extend(WorkspaceTopology.TABLE).createWithoutData(workspaceId);
      const parseUser = this.parse.User.createWithoutData(userId);
      const parseMember = new this.parse.Object(MemberTopology.TABLE);

      parseMember.set(MemberTopology.WORKSPACE, parseWorkspace);
      parseMember.set(MemberTopology.USER, parseUser);

      parseMemberships.push(parseMember);
    }

    const savedMemberships = await this.parse.Object.saveAll(parseMemberships, {
      ...this.options,
      batchSize: batchSize,
    });
    return savedMemberships.map((membership) => ({
      workspaceId: membership.get(MemberTopology.WORKSPACE)?.id,
      membershipId: membership.id,
    }));
  }

  public async saveMember(member: Member): Promise<string> {
    const { id } = await this.memberMapper.fromDomain(member).save(null, this.options);

    return id;
  }

  public async deleteMember(memberId: string): Promise<void> {
    const member = this.subclasses.getSubclass(MemberTopology.TABLE).createWithoutData(memberId);
    await member.destroy(this.options);
  }

  public async save(workspace: Workspace): Promise<string> {
    const { id } = await this.workspaceMapper.fromDomain(workspace).save(null, this.options);

    return id;
  }

  public async saveAll(...workspaces: Workspace[]): Promise<string[]> {
    const parseWorkspaces = workspaces.map((w) => this.workspaceMapper.fromDomain(w));

    const results = await this.parse.Object.saveAll(parseWorkspaces, this.options);

    return results.map(({ id }) => id);
  }

  public async delete(workspaceId: string): Promise<boolean> {
    try {
      const reference = this.subclasses.getSubclass(WorkspaceTopology.TABLE).createWithoutData(workspaceId);
      const update = { [SoftDeletableTopology.DELETED_AT]: new Date() };
      await reference.save(update, this.options);
      return true;
    } catch {
      return false;
    }
  }

  public async isMemberOfWorkspace(userId: string, workspaceId: string): Promise<string | undefined> {
    const parseUser = this.parse.User.createWithoutData(userId);
    const parseWorkspace = this.subclasses.getSubclass(WorkspaceTopology.TABLE).createWithoutData(workspaceId);

    const query = new this.parse.Query(MemberTopology.TABLE)
      .equalTo(MemberTopology.USER, parseUser)
      .equalTo(MemberTopology.WORKSPACE, parseWorkspace);

    // Only fetch objectIds to minimize data transfer
    query.select();

    const member = await query.first(this.options);
    return member?.id;
  }

  public async listMembers(
    workspaceId: string,
    limit: number,
    searchTerm?: string,
    lastMemberId?: string
  ): Promise<Member[]> {
    const parseWorkspace = this.subclasses.getSubclass(WorkspaceTopology.TABLE).createWithoutData(workspaceId);
    const query = new this.parse.Query(MemberTopology.TABLE)
      .equalTo(MemberTopology.WORKSPACE, parseWorkspace)
      .ascending(ObjectTopology.ID);

    if (searchTerm) {
      const userQuery = new this.parse.Query(this.parse.User).matches(
        UserTopology.USERNAME,
        new RegExp(this.escapeRegExp(searchTerm), 'i')
      );
      query.matchesQuery(MemberTopology.USER, userQuery);
    }

    if (lastMemberId) {
      query.greaterThan(ObjectTopology.ID, lastMemberId);
    }

    if (limit) {
      query.limit(limit);
    }

    query.include(MemberTopology.USER, MemberTopology.WORKSPACE);

    const parseMemberships = await query.find(this.options);
    return parseMemberships.map((parseMembership) => this.memberMapper.toDomain(parseMembership));
  }
}
